// b16: Victron i appen, bara läsning. Under Inställningar → Victron, och i guiden:
//
//   - GX-enhetens adress på platsens nät (venus.local som förslag när navet hittar den), och VRM
//     Portal ID om navet inte hittar det själv. Navet läser GX-enheten via MQTT, och skriver
//     aldrig till den.
//   - Banken för varje batteriövervakare: förbrukningsbanken, navigationsbanken eller ingen.
//   - Nyckeln för varje enhet över Bluetooth. Den förseglas här i webbläsaren med navets publika
//     nyckel, och bara lådan skickas: molnet kan inte läsa nyckeln.
//   - b17: GX-enhetens användarnamn och lösenord för MQTT, när säkerhetsprofilen kräver det.
//     De förseglas som nycklarna, och navet ansluter då med TLS (port 8883).

import {
  GX_ADRESS,
  GX_ANVANDARE,
  GX_LOSENORD,
  NYCKEL_HJALP,
  type Platsinstallningar,
  PORTAL_ID,
  VICTRON_NYCKEL,
  VICTRONBANKAR,
  VICTRONBANKNAMN,
  type Victronbank,
  type Victronenhet,
} from "@batkoll/gemensam";
import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import { api } from "../api.ts";
import { Kort } from "../komponenter.tsx";

const NYCKELTEXT: Record<string, string> = {
  saknas: "nyckel saknas",
  fel: "nyckeln stämmer inte",
  finns: "nyckeln finns i navet",
  skickad: "nyckeln är på väg till navet",
};

/** Nyckelns läge i ord, för en enhet över Bluetooth. */
export function nyckeltext(n: string | null | undefined): string {
  return n ? (NYCKELTEXT[n] ?? n) : "";
}

/** Fältet för en nyckel ur VictronConnect. Nyckeln stannar i webbläsaren tills den förseglas. */
export function Nyckelfalt(props: { namn: string; varde: string; satt: (v: string) => void }) {
  const fel = props.varde.trim() !== "" && !VICTRON_NYCKEL.test(props.varde.trim());
  return (
    <label>
      Nyckeln för {props.namn} (32 tecken)
      <input
        type="password"
        autoComplete="off"
        spellcheck={false}
        maxLength={32}
        value={props.varde}
        aria-invalid={fel}
        onInput={(e) => props.satt((e.target as HTMLInputElement).value)}
      />
      {fel && <span class="liten">Nyckeln har 32 tecken, 0–9 och a–f.</span>}
    </label>
  );
}

/** GX-enhetens adress och VRM Portal ID. */
export function Gxfalt(props: {
  adress: string;
  portal: string;
  forslag: string | null;
  satt: (a: { adress: string; portal: string }) => void;
}) {
  const adressFel = props.adress.trim() !== "" && !GX_ADRESS.test(props.adress.trim());
  const portalFel =
    props.portal.trim() !== "" && !PORTAL_ID.test(props.portal.trim().toLowerCase());
  return (
    <>
      <label>
        GX-enhetens adress på platsens nät
        <input
          type="text"
          inputMode="url"
          autoComplete="off"
          placeholder={props.forslag ?? "till exempel 192.168.1.40"}
          value={props.adress}
          aria-invalid={adressFel}
          onInput={(e) =>
            props.satt({ adress: (e.target as HTMLInputElement).value, portal: props.portal })
          }
        />
      </label>
      {props.forslag && props.adress.trim() === "" && (
        <p class="liten">
          Navet hittar en GX-enhet på {props.forslag}.{" "}
          <button
            type="button"
            class="knapp-lank"
            onClick={() => props.satt({ adress: props.forslag ?? "", portal: props.portal })}
          >
            Använd {props.forslag}
          </button>
        </p>
      )}
      <label>
        VRM Portal ID, om navet inte hittar det (valfritt)
        <input
          type="text"
          autoComplete="off"
          maxLength={12}
          value={props.portal}
          aria-invalid={portalFel}
          onInput={(e) =>
            props.satt({ adress: props.adress, portal: (e.target as HTMLInputElement).value })
          }
        />
      </label>
      <p class="liten">
        Slå på MQTT on LAN i GX-enheten (Settings → Services). Navet läser bara, och skriver aldrig
        till Victron.
      </p>
    </>
  );
}

const LOSENORDSTEXT: Record<string, string> = {
  ingen: "Navet har inget lösenord för GX-enheten.",
  skickad: "Lösenordet är på väg till navet, förseglat.",
  mottagen: "Navet har lösenordet, krypterat.",
};

const INLOGGNINGSTEXT: Record<string, string> = {
  utan: "Navet loggar in utan lösenord.",
  lyckades: "Inloggningen på GX-enheten lyckades.",
  fel: "Inloggningen gick inte: användarnamnet eller lösenordet stämmer inte.",
  kravs: "GX-enheten kräver ett lösenord. Skriv in det nedan.",
};

/** b17: GX-enhetens lösenordets och inloggningens läge i ord. Aldrig lösenordet. */
export function losenordstext(
  losenord: string | undefined,
  inloggning: string | null | undefined,
): string {
  return [
    losenord ? LOSENORDSTEXT[losenord] : null,
    inloggning ? INLOGGNINGSTEXT[inloggning] : null,
  ]
    .filter(Boolean)
    .join(" ");
}

/**
 * b17: GX-enhetens användarnamn och lösenord för MQTT. De stannar i webbläsaren tills de
 * förseglas till navet.
 */
export function Gxlosenordsfalt(props: {
  anvandare: string;
  losenord: string;
  satt: (v: { anvandare: string; losenord: string }) => void;
}) {
  const anvFel = !GX_ANVANDARE.test(props.anvandare.trim());
  const losFel = props.losenord !== "" && !GX_LOSENORD.test(props.losenord);
  return (
    <>
      <label>
        GX-enhetens användarnamn för MQTT (oftast tomt)
        <input
          type="text"
          autoComplete="off"
          spellcheck={false}
          maxLength={64}
          value={props.anvandare}
          aria-invalid={anvFel}
          onInput={(e) => props.satt({ ...props, anvandare: (e.target as HTMLInputElement).value })}
        />
      </label>
      <label>
        GX-enhetens lösenord (säkerhetsprofilen Weak eller Secured)
        <input
          type="password"
          autoComplete="off"
          spellcheck={false}
          maxLength={128}
          value={props.losenord}
          aria-invalid={losFel}
          onInput={(e) => props.satt({ ...props, losenord: (e.target as HTMLInputElement).value })}
        />
      </label>
      <p class="liten">
        Lösenordet är det som är satt i GX-enheten under Settings → General → Network security
        profile. Det förseglas här till navet, och molnet kan inte läsa det. Med lösenordet ansluter
        navet med TLS (port 8883).
      </p>
    </>
  );
}

/** Inställningar → Victron. */
export function Victrondel(props: {
  s: Platsinstallningar;
  spara: (
    sektion: string,
    gor: () => Promise<Platsinstallningar | [Platsinstallningar, string]>,
    ok?: string,
  ) => Promise<boolean>;
  beskedrad: ComponentChildren;
}) {
  const v = props.s.victron;
  const plats = props.s.plats.id;
  const [gx, setGx] = useState({ adress: v?.gx.adress ?? "", portal: v?.gx.portal ?? "" });
  const [nycklar, setNycklar] = useState<Record<string, string>>({});
  const [inloggning, setInloggning] = useState({ anvandare: "", losenord: "" });
  if (!v) return null;
  const adressOk = gx.adress.trim() === "" || GX_ADRESS.test(gx.adress.trim());
  const portalOk = gx.portal.trim() === "" || PORTAL_ID.test(gx.portal.trim().toLowerCase());

  async function sparaNyckel(e: Victronenhet) {
    const n = (nycklar[e.fynd] ?? "").trim();
    if (!e.lada) return;
    const ok = await props.spara(
      "victron",
      () => api.victronNyckel(plats, e.fynd, e.lada as string, n),
      `Nyckeln för ${e.namn} förseglades och skickades till navet. Molnet kan inte läsa den.`,
    );
    if (ok) setNycklar({ ...nycklar, [e.fynd]: "" });
  }

  async function sparaLosenord(tom: boolean) {
    if (!v?.lada) return;
    const innehall = tom
      ? { anvandare: "", losenord: "" }
      : { anvandare: inloggning.anvandare.trim(), losenord: inloggning.losenord };
    const ok = await props.spara(
      "victron",
      () => api.hemlighet(plats, "gx", v.lada as string, innehall),
      tom
        ? "Navet tar bort GX-enhetens lösenord."
        : "GX-enhetens lösenord förseglades och skickades till navet. Molnet kan inte läsa det.",
    );
    if (ok) setInloggning({ anvandare: "", losenord: "" });
  }

  return (
    <Kort rubrik="Victron" bred>
      <p class="liten">
        Victron läses bara: GX-enheten via MQTT på platsens nät, och enheterna över Bluetooth med en
        nyckel per enhet. Statusen är beta tills en riktig enhet är provad.
      </p>
      <form
        class="formular"
        aria-label="Victrons GX-enhet"
        onSubmit={(e) => {
          e.preventDefault();
          void props.spara("victron", () =>
            api.andraPlats(plats, {
              victron: {
                gx: { adress: gx.adress.trim() || null, portal: gx.portal.trim() || null },
              },
            }),
          );
        }}
      >
        <Gxfalt adress={gx.adress} portal={gx.portal} forslag={v.forslag} satt={setGx} />
        {v.gx.adress && (
          <p class="liten">
            {v.kontakt === true
              ? "Navet har kontakt med GX-enheten."
              : v.kontakt === false
                ? "Navet har ingen kontakt med GX-enheten. Är MQTT on LAN på, och stämmer adressen?"
                : "Navet har inte sagt något om GX-enheten än."}
          </p>
        )}
        <div class="knapprad">
          <button type="submit" class="knapp-andra" disabled={!adressOk || !portalOk}>
            Spara GX-enheten
          </button>
        </div>
      </form>
      {v.gx.adress && (
        <form
          class="formular"
          aria-label="GX-enhetens lösenord"
          onSubmit={(e) => {
            e.preventDefault();
            void sparaLosenord(false);
          }}
        >
          <p class="liten">{losenordstext(v.losenord, v.inloggning)}</p>
          {v.lada ? (
            <>
              <Gxlosenordsfalt
                anvandare={inloggning.anvandare}
                losenord={inloggning.losenord}
                satt={setInloggning}
              />
              <div class="knapprad">
                <button
                  type="submit"
                  class="knapp-andra"
                  disabled={
                    !GX_LOSENORD.test(inloggning.losenord) ||
                    !GX_ANVANDARE.test(inloggning.anvandare.trim())
                  }
                >
                  Försegla och spara lösenordet
                </button>
                {v.losenord === "mottagen" && (
                  <button
                    type="button"
                    class="knapp-andra"
                    onClick={() => void sparaLosenord(true)}
                  >
                    Ta bort lösenordet
                  </button>
                )}
              </div>
            </>
          ) : (
            <p class="liten">
              Navet kan inte ta emot ett lösenord än: det behöver navets version från b17.
            </p>
          )}
        </form>
      )}
      {v.enheter.length === 0 ? (
        <p class="liten">
          Navet hör inga Victron-enheter än. En enhet över Bluetooth syns här när navet hör den, och
          tjänsterna i GX-enheten när navet läser den.
        </p>
      ) : (
        <div class="formular">
          {v.enheter.map((e) => (
            <div key={e.fynd} class="victronenhet">
              <strong>{e.namn}</strong>
              {e.kalla === "bluetooth" && (
                <span class="liten">Över Bluetooth: {nyckeltext(e.nyckel)}.</span>
              )}
              {e.batteri && (
                <label>
                  Banken som {e.namn} mäter
                  <select
                    value={e.bank}
                    onChange={(x) => {
                      const bank = (x.target as HTMLSelectElement).value as Victronbank;
                      void props.spara("victron", () =>
                        api.andraPlats(plats, { victron: { bankar: { [e.fynd]: bank } } }),
                      );
                    }}
                  >
                    {VICTRONBANKAR.map((b) => (
                      <option key={b} value={b}>
                        {VICTRONBANKNAMN[b]}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {e.kalla === "bluetooth" &&
                (e.lada ? (
                  <form
                    class="formular"
                    onSubmit={(x) => {
                      x.preventDefault();
                      void sparaNyckel(e);
                    }}
                  >
                    <Nyckelfalt
                      namn={e.namn}
                      varde={nycklar[e.fynd] ?? ""}
                      satt={(n) => setNycklar({ ...nycklar, [e.fynd]: n })}
                    />
                    <div class="knapprad">
                      <button
                        type="submit"
                        class="knapp-andra"
                        disabled={!VICTRON_NYCKEL.test((nycklar[e.fynd] ?? "").trim())}
                      >
                        Försegla och spara nyckeln
                      </button>
                    </div>
                  </form>
                ) : (
                  <p class="liten">
                    Navet kan inte ta emot nycklar än: det behöver Bluetooth och navets version från
                    b16.
                  </p>
                ))}
            </div>
          ))}
          <p class="liten">{NYCKEL_HJALP}</p>
        </div>
      )}
      {props.beskedrad}
    </Kort>
  );
}
